import { Helmet } from "react-helmet-async";
import ShippingDisclaimer from "../components/ShippingDisclaimer";

export default function ShippingPolicy() {
  return (
    <>
      <Helmet>
        <title>Shipping Policy | Kratoven</title>
        <meta
          name="description"
          content="Fast discreet DHL Express shipping from Borneo. View Kratoven shipping times, rates, customs information, and restricted destinations."
        />
        <link rel="canonical" href="https://kratoven.com/shipping" />
      </Helmet>

      <div className="container-kv pt-32 pb-20 max-w-3xl" data-testid="shipping-policy-page">
      <div className="overline mb-4">Shipping Policy</div>
      <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter mb-10">
        Fast. Discreet. <em className="italic text-[#C8A97E]">Everywhere possible.</em>
      </h1>

      <div className="mb-10">
        <ShippingDisclaimer />
      </div>

      <div className="space-y-8 text-[#A8A5A1] leading-relaxed">
        <Section title="Processing time">
          All orders are processed and shipped from our Borneo facility within 1 business day. You will receive a DHL Express tracking number by email as soon as your package is handed to the courier.
        </Section>
        <Section title="DHL Express — worldwide">
          Every Kratoven order ships via <strong>DHL Express</strong> from Borneo, Indonesia.
          <br />
          Typical transit windows:
          <br />• United States / Canada — 3–5 business days
          <br />• Europe — 4–7 business days
          <br />• Asia (permitted) — 2–4 business days
          <br />• Rest of world — 5–10 business days
        </Section>
        <Section title="How shipping is calculated">
          Shipping is calculated at checkout using DHL Express published rates for your destination and the billable weight of your order (minimum 250g). We do not offer free shipping — you always see the real DHL rate before you pay.
          <br /><br />
          <div className="overflow-x-auto mt-4">
            <table className="w-full text-sm border-collapse" data-testid="dhl-zone-table">
              <thead>
                <tr className="text-xs text-[#75726D] uppercase tracking-widest">
                  <th className="py-2 px-3 border-b border-white/10 text-left">Zone</th>
                  <th className="py-2 px-3 border-b border-white/10 text-left">Destinations</th>
                  <th className="py-2 px-3 border-b border-white/10 text-right">Base</th>
                  <th className="py-2 px-3 border-b border-white/10 text-right">Per kg</th>
                  <th className="py-2 px-3 border-b border-white/10 text-right">Transit</th>
                </tr>
              </thead>
              <tbody className="text-[#A8A5A1]">
                <tr>
                  <td className="py-2 px-3 border-b border-white/5 text-[#C8A97E]">A</td>
                  <td className="py-2 px-3 border-b border-white/5">United States, Canada, Mexico</td>
                  <td className="py-2 px-3 border-b border-white/5 text-right">$24–28</td>
                  <td className="py-2 px-3 border-b border-white/5 text-right">$12–14</td>
                  <td className="py-2 px-3 border-b border-white/5 text-right">3–5 bd</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 border-b border-white/5 text-[#C8A97E]">B</td>
                  <td className="py-2 px-3 border-b border-white/5">Germany, France, Spain, Italy, Netherlands, Belgium, Ireland, Austria, Switzerland, Norway, Portugal, Greece</td>
                  <td className="py-2 px-3 border-b border-white/5 text-right">$30–34</td>
                  <td className="py-2 px-3 border-b border-white/5 text-right">$15–17</td>
                  <td className="py-2 px-3 border-b border-white/5 text-right">4–7 bd</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 border-b border-white/5 text-[#C8A97E]">C</td>
                  <td className="py-2 px-3 border-b border-white/5">Philippines, India</td>
                  <td className="py-2 px-3 border-b border-white/5 text-right">$16–18</td>
                  <td className="py-2 px-3 border-b border-white/5 text-right">$9–10</td>
                  <td className="py-2 px-3 border-b border-white/5 text-right">2–4 bd</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 border-b border-white/5 text-[#C8A97E]">D</td>
                  <td className="py-2 px-3 border-b border-white/5">UAE, Saudi Arabia</td>
                  <td className="py-2 px-3 border-b border-white/5 text-right">$28–30</td>
                  <td className="py-2 px-3 border-b border-white/5 text-right">$14–15</td>
                  <td className="py-2 px-3 border-b border-white/5 text-right">3–6 bd</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 text-[#C8A97E]">E</td>
                  <td className="py-2 px-3">Brazil &amp; rest of world (where permitted)</td>
                  <td className="py-2 px-3 text-right">$38</td>
                  <td className="py-2 px-3 text-right">$20</td>
                  <td className="py-2 px-3 text-right">5–10 bd</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-[#75726D] mt-3">
            Rates are indicative. Exact amount is calculated at checkout based on the billable weight (rounded up to the nearest 250g).
          </p>
        </Section>
        <Section title="Import duties & taxes">
          Your total includes products and DHL Express shipping. <strong>Import duties, VAT, and any local taxes are the recipient's responsibility</strong> and are collected by customs on arrival. Kratoven cannot pre-pay or waive these charges, and parcels cannot be under-declared.
        </Section>
        <Section title="Packaging">
          Every order is vacuum-sealed in our signature matte-black resealable foil and shipped in unmarked DHL packaging. The return address is neutral. There are no external mentions of the contents.
        </Section>
        <Section title="Restricted jurisdictions">
          Due to local regulations we cannot ship kratom to the following destinations. Orders placed to these addresses will be refunded automatically.
          <br /><br />
          <strong>Indonesia</strong> (our home country — domestic sales are prohibited).<br />
          <strong>US states:</strong> Alabama, Arkansas, Indiana, Louisiana, Vermont, Wisconsin.<br />
          <strong>Countries:</strong> Australia, Denmark, Finland, Israel, Japan, Malaysia, Myanmar, New Zealand, Poland, Romania, Russia, Singapore, South Korea, Sweden, Thailand, United Kingdom, Vietnam.
        </Section>
        <Section title="Customs">
          International customers are responsible for any applicable customs duties or taxes. We cannot mark parcels as gifts or under-declare value.
        </Section>
      </div>
    </div>
    </>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="font-serif text-xl text-[#F4F1ED] mb-3">{title}</h3>
      <div>{children}</div>
    </div>
  );
}
